import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { GitBranch, RefreshCw } from 'lucide-react'
import type { RepoGit } from '@one-deployment/api'
import { enc, get, post } from '@/lib/api'
import { RoleGate } from '@/components/RoleGate'
import { Button } from '@/components/shadcn/button'
import { qk } from '@/lib/query'
import { cn } from '@/lib/cn'
import { FilesClient, type FilesGit } from '@/lib/files'
import { ReleaseButtons } from './Release'

/** The repo's git picture, polled slowly: GitHub is the source, and a PR's checks change in minutes, not seconds. */
export function useRepoGit(project: string) {
  return useQuery({
    queryKey: qk.projectGit(project),
    queryFn: ({ signal }) => get<RepoGit>(`/api/projects/${enc(project)}/git`, signal),
    refetchInterval: 60_000,
  })
}

/** The session folder's own git state, from the box: asked on connect and every 30s (the box fetches every 3 min). */
function useSessionGit(sessionId: string, base: string | undefined): FilesGit | null {
  const [git, setGit] = useState<FilesGit | null>(null)
  useEffect(() => {
    setGit(null)
    const c: FilesClient = new FilesClient(sessionId, 'ro', {
      ready: () => c.git(base), listing: () => {}, state: () => {}, git: setGit,
    })
    const t = setInterval(() => c.git(base), 30_000)
    return () => { clearInterval(t); c.stop() }
  }, [sessionId, base])
  return git
}

/** Push to prod for the whole project, only where the project file names a prod workflow, in the top bar on every project page. */
export function ProjectRelease({ project }: { project: string }) {
  const { data } = useRepoGit(project)
  if (!data) return null
  // Always shown, so it is never "gone": without a prod pipeline in the applied project file it says what is missing.
  if (!data.ship) return <Button variant="outline" size="xs" disabled title="Not set up for this project yet: its project file (.trisof/project.yml on the base branch) needs a ship section" className="h-5 rounded-sm font-normal text-fg-subtle">Push to prod</Button>
  return <span className="text-xs text-fg-muted"><ReleaseButtons project={project} base={data.base} prod={data.prod} prs={data.prs.length} shipCi={data.shipCi ?? null} pushes={data.pushes ?? []} /></span>
}

/**
 * Beside the session's name in the breadcrumbs: its branch, and Sync, which
 * asks Claude to bring this session's worktree up to date with the base
 * branch. The rest (PRs, prod) lives in the top bar's Push to prod.
 */
export function SessionGit({ project, sessionId }: { project: string; sessionId: string }) {
  const { data } = useRepoGit(project)
  const local = useSessionGit(sessionId, data?.base)
  const sync = useMutation({ mutationFn: () => post<{ ok: true }>(`/api/sessions/${enc(sessionId)}/sync`) })
  return (
    <div className="flex min-w-0 shrink-0 items-center gap-2 text-xs text-fg-muted">
      <span className="flex items-center gap-1"><GitBranch size={12} />{local ? (local.branch ?? 'detached') : '…'}</span>
      <RoleGate action="session.manage" project={project}>
        <Button variant="ghost" size="xs" title={`Ask Claude to pull ${data?.base ?? 'the base branch'} into this session's worktree`} disabled={sync.isPending}
          className="h-5 gap-1 rounded-xs px-1.5 font-normal hover:bg-hover disabled:opacity-40 has-[>svg]:px-1.5"
          onClick={() => sync.mutate()}><RefreshCw size={11} className={cn(sync.isPending && 'animate-spin')} />Sync</Button>
      </RoleGate>
      {sync.isSuccess && <span className="text-fg-subtle">sent to Claude</span>}
      {sync.error && <span className="text-err">{(sync.error as Error).message}</span>}
    </div>
  )
}
